import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import camelCase from 'lodash/camelCase';
import { useApiClient } from '@brewardapp/api-client';

interface Difference {
    actual: any;
    details: any;
    expected: any;
    message: string;
    rule: string;
    ruleType: string;
    section: string;
    subsection: string;
}

interface SubsectionResult {
    differences: Difference[];
    passedChecks: number;
    subsection: string;
    totalChecks: number;
}

interface SectionResult {
    differences: Difference[];
    passedChecks: number;
    section: string;
    totalChecks: number;
    subsectionResults: SubsectionResult[];
}

interface ScoresheetField {
    id: string;
    key: string;
    label: string;
    order: number;
    fieldType: string;
    description: string;
    settings: {
        [key: string]: any;
    };
}

interface ScoresheetSubsection {
    id: string;
    key: string;
    label: string;
    order: number;
    fields: ScoresheetField[];
}

interface ScoresheetSection {
    id: string;
    key: string;
    label: string;
    order: number;
    subsections: ScoresheetSubsection[];
}

interface SectionComparisonViewProps {
    sectionResult: SectionResult;
    scoresheetValues?: any;
    scoresheetSchema?: {
        data?: {
            sections?: ScoresheetSection[];
        };
    };
}

// Component to handle descriptor UUID fetching
interface DescriptorValueProps {
    descriptorId: string;
    intensity?: string | number;
}

// Helper function to get labels from field settings
const getFieldLabels = (field: ScoresheetField) => {
    const { settings } = field;
    if (settings?.labels && Array.isArray(settings.labels)) {
        return settings.labels;
    }
    if (settings?.availableValues && Array.isArray(settings.availableValues)) {
        return settings.availableValues;
    }
    if (settings?.values && Array.isArray(settings.values)) {
        return settings.values;
    }
    return null;
};

const DescriptorValue = ({ descriptorId, intensity }: DescriptorValueProps) => {
    const api = useApiClient();
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(descriptorId);

    const { data: descriptorData, isLoading } = api.descriptors.useFindDescriptor(
        {
            filter: {
                id: descriptorId,
            },
        },
        {
            enabled: isUUID,
        },
    ) as { data: { data?: { name?: string } } | undefined; isLoading: boolean };

    if (isLoading && isUUID) {
        return <span className="inline-flex items-center gap-1"><div className="icon-[ph--spinner] h-3 w-3 animate-spin" /> Loading...</span>;
    }

    const displayName = isUUID ? descriptorData?.data?.name || descriptorId : descriptorId;

    return (
        <span>
            {displayName}
            {intensity && ` (${intensity})`}
        </span>
    );
};

// Component to render formatted values based on field type and schema
interface FormattedValueProps {
    value: any;
    field: ScoresheetField;
}

const FormattedValue = ({ value, field }: FormattedValueProps) => {
    const [t] = useTranslation();

    if (value === null || value === undefined) {
        return <span className="text-content-500 dark:text-content-400 italic">{t('Pages:ScoresheetPage.NotProvided', 'Not provided')}</span>;
    }

    // Helper function to map value to label
    const mapValueToLabel = (val: any) => {
        const labels = getFieldLabels(field);
        if (!labels) return val;

        if (typeof val === 'number' && labels[val] !== undefined) {
            return labels[val];
        }

        // For string values, try to find exact match or snake_case conversion
        if (typeof val === 'string') {
            const exactMatch = labels.find((label: string) => label === val);
            if (exactMatch) return exactMatch;

            // Try to find by converting snake_case to normal case
            const normalizedVal = val.replace(/_/g, ' ').toLowerCase();
            const labelMatch = labels.find((label: string) =>
                label.toLowerCase() === normalizedVal
            );
            if (labelMatch) return labelMatch;
        }

        return val;
    };

    switch (field.fieldType) {
        case 'descriptor':
            if (Array.isArray(value)) {
                if (value.length === 0) {
                    return <span className="text-content-500 dark:text-content-400 italic">{t('Pages:ScoresheetPage.NotProvided', 'Not provided')}</span>;
                }
                return (
                    <div className="space-y-1">
                        {value.map((item, index) => (
                            <div key={index} className="inline-block mr-2 mb-1">
                                <DescriptorValue
                                    descriptorId={item.value || item}
                                    intensity={item.intensity}
                                />
                            </div>
                        ))}
                    </div>
                );
            }
            return <DescriptorValue descriptorId={value} />;

        case 'slider':
            const labelValue = mapValueToLabel(value);
            return <span>{labelValue} {labelValue !== value && <span className="text-content-500">({value})</span>}</span>;

        case 'select':
            if (Array.isArray(value)) {
                return <span>{value.map(mapValueToLabel).join(', ')}</span>;
            }
            return <span>{mapValueToLabel(value)}</span>;

        case 'checkbox':
            return (
                <span className={`inline-flex items-center gap-1 ${value ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'}`}>
                    <div className={`i-ph-${value ? 'check' : 'x'} h-4 w-4`} />
                    {value ? t('Pages:ScoresheetPage.Yes', 'Yes') : t('Pages:ScoresheetPage.No', 'No')}
                </span>
            );

        case 'graph':
            if (Array.isArray(value)) {
                const labels = getFieldLabels(field);
                return (
                    <span>
                        {value.map((val, index) => `${labels[index] || `Item ${index + 1}`}: ${val}`).join(', ')}
                    </span>
                );
            }
            return <span>{String(value)}</span>;

        case 'palette':
            return <span>{value} EBC</span>;

        case 'textarea':
            return <span className="whitespace-pre-wrap">{String(value)}</span>;

        default:
            if (Array.isArray(value)) {
                return <span>{value.join(', ')}</span>;
            }
            if (typeof value === 'object' && value !== null) {
                if (value.min !== undefined && value.max !== undefined) {
                    return <span>{value.min} - {value.max}</span>;
                }
                return <span>{JSON.stringify(value)}</span>;
            }
            return <span>{String(value)}</span>;
    }
};

const SectionComparisonView = ({ sectionResult, scoresheetValues, scoresheetSchema }: SectionComparisonViewProps) => {
    const [t] = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);

    const sectionPercentage = (sectionResult.passedChecks / sectionResult.totalChecks) * 100;

    // Get the schema section for this comparison section
    const getSchemaSection = (): ScoresheetSection | undefined => {
        return scoresheetSchema?.data?.sections?.find(
            section => section.key === sectionResult.section
        );
    };

    // Get the actual value from scoresheet for a specific field
    const getFieldValue = (sectionKey: string, subsectionKey: string, fieldKey: string) => {
        return scoresheetValues?.[camelCase(sectionKey)]?.[camelCase(subsectionKey)]?.[camelCase(fieldKey)];
    };

    // Check if a field has a difference
    const hasFieldDifference = (sectionKey: string, subsectionKey: string, fieldKey: string): Difference | undefined => {
        return sectionResult.differences.find(diff =>
            diff.section === sectionKey &&
            diff.subsection === subsectionKey &&
            diff.rule === fieldKey
        );
    };

    const getSectionStatusColor = (percentage: number) => {
        if (percentage >= 80) return 'text-success-600 dark:text-success-400';
        if (percentage >= 60) return 'text-warning-600 dark:text-warning-400';
        return 'text-error-600 dark:text-error-400';
    };

    const getSectionStatusBg = (percentage: number) => {
        if (percentage >= 80) return 'bg-success-50 dark:bg-success-900/20 border-success-200 dark:border-success-800';
        if (percentage >= 60) return 'bg-warning-50 dark:bg-warning-900/20 border-warning-200 dark:border-warning-800';
        return 'bg-error-50 dark:bg-error-900/20 border-error-200 dark:border-error-800';
    };

    const getSectionStatusIcon = (percentage: number) => {
        if (percentage >= 80) return 'icon-[ph--check-circle]';
        if (percentage >= 60) return 'icon-[ph--warning-circle]';
        return 'icon-[ph--x-circle]';
    };


    // Render a single field value with its status
    const renderFieldValue = (field: ScoresheetField, sectionKey: string, subsectionKey: string) => {
        const fieldValue = getFieldValue(sectionKey, subsectionKey, field.key);
        const difference = hasFieldDifference(sectionKey, subsectionKey, field.key);
        const hasValue = fieldValue !== null && fieldValue !== undefined &&
            (Array.isArray(fieldValue) ? fieldValue.length > 0 : true);

        if (!hasValue && !difference) {
            return null; // Don't show fields with no value and no difference
        }

        const isMatching = !difference;

        return (
            <div key={field.key} className={`font-header p-3 rounded-lg border ${isMatching
                ? 'bg-success-50 dark:bg-success-900/20 border-success-200 dark:border-success-800'
                : 'bg-error-50 dark:bg-error-900/20 border-error-200 dark:border-error-800'
                }`}>
                <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <div className={`${isMatching ? 'icon-[ph--check-circle] text-success-600 dark:text-success-400' : 'icon-[ph--warning-circle] text-error-600 dark:text-error-400'} h-4 w-4`} />
                        <span className="font-medium font-sans text-content-900 dark:text-content-100">
                            {field.label}
                        </span>
                    </div>
                    <span className={`text-xs font-medium font-sans px-2 py-1 rounded-full ${isMatching
                        ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300'
                        : 'bg-error-100 dark:bg-error-900/30 text-error-700 dark:text-error-300'
                        }`}>
                        {isMatching ? t('Pages:ScoresheetPage.Matches', 'Matches') : t('Pages:ScoresheetPage.Issue', 'Issue')}
                    </span>
                </div>

                <div className="space-y-2">
                    <div>
                        <div className="text-xs font-medium font-sans text-content-600 dark:text-content-400 uppercase tracking-wide mb-1">
                            {t('Pages:ScoresheetPage.JudgeValue', 'Judge Value')}
                        </div>
                        <div className={`p-2 rounded-md border font-mono text-sm ${isMatching
                            ? 'bg-success-100 dark:bg-success-900/30 border-success-200 dark:border-success-800 text-success-800 dark:text-success-200'
                            : 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800 text-primary-800 dark:text-primary-200'
                            }`}>
                            <FormattedValue value={fieldValue} field={field} />
                        </div>
                    </div>

                    {difference && (
                        <div className="space-y-2">
                            <div className="flex items-start gap-2 p-3 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg">
                                <div className="icon-[ph--warning-circle] h-4 w-4 text-error-600 dark:text-error-400 mt-0.5 flex-shrink-0" />
                                <div className="flex-1 space-y-2">
                                    <div className="text-sm font-medium text-error-800 dark:text-error-200">
                                        {t('Pages:ScoresheetPage.Issue', 'Issue')}
                                    </div>
                                    <div className="text-sm text-error-700 dark:text-error-300">
                                        {difference.message}
                                    </div>

                                    {/* Expected Value Display */}
                                    {difference.expected !== undefined && difference.expected !== null && (
                                        <div className="mt-3 p-2 bg-accent-50 dark:bg-accent-900/20 border border-accent-200 dark:border-accent-800 rounded-md">
                                            <div className="flex items-center gap-2 mb-1">
                                                <div className="icon-[ph--target] h-3 w-3 text-accent-600 dark:text-accent-400" />
                                                <span className="text-xs font-medium text-accent-800 dark:text-accent-200 uppercase tracking-wide">
                                                    {t('Pages:ScoresheetPage.Expected', 'Expected')}
                                                </span>
                                            </div>
                                            <div className="text-sm text-accent-700 dark:text-accent-300">
                                                {(() => {
                                                    if (difference.ruleType === 'range' && typeof difference.expected === 'object' && difference.expected.min !== undefined && difference.expected.max !== undefined) {
                                                        return (
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-mono bg-accent-100 dark:bg-accent-800 px-2 py-1 rounded text-xs">
                                                                    {difference.expected.min} - {difference.expected.max}
                                                                </span>
                                                                <span className="text-xs text-accent-600 dark:text-accent-400">range</span>
                                                            </div>
                                                        );
                                                    } else if (difference.ruleType === 'balance' && Array.isArray(difference.expected)) {
                                                        const labels = getFieldLabels(field);
                                                        return (
                                                            <div className="flex flex-wrap gap-1">
                                                                {difference.expected.map((val: any, idx: number) => (
                                                                    <span key={idx} className="inline-flex items-center gap-1 bg-accent-100 dark:bg-accent-800 px-2 py-1 rounded text-xs font-mono">
                                                                        <span className="text-accent-600 dark:text-accent-400">{labels[idx] || `Item ${idx + 1}`}:</span>
                                                                        <span>{val}</span>
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        );
                                                    } else if (difference.ruleType === 'boolean') {
                                                        return (
                                                            <div className="flex items-center gap-2">
                                                                <div className={`i-ph-${difference.expected ? 'check' : 'x'} h-4 w-4 ${difference.expected ? 'text-success-600' : 'text-error-600'}`} />
                                                                <span className="font-medium">{difference.expected ? t('Pages:ScoresheetPage.Yes', 'Yes') : t('Pages:ScoresheetPage.No', 'No')}</span>
                                                            </div>
                                                        );
                                                    } else if (Array.isArray(difference.expected)) {
                                                        return (
                                                            <div className="flex flex-wrap gap-1">
                                                                {difference.expected.map((val: any, idx: number) => (
                                                                    <span key={idx} className="bg-accent-100 dark:bg-accent-800 px-2 py-1 rounded text-xs font-mono">
                                                                        {field ? <FormattedValue value={val} field={field} /> : String(val)}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        );
                                                    } else {
                                                        return (
                                                            <span className="font-mono bg-accent-100 dark:bg-accent-800 px-2 py-1 rounded text-sm">
                                                                {field ? <FormattedValue value={difference.expected} field={field} /> : String(difference.expected)}
                                                            </span>
                                                        );
                                                    }
                                                })()}
                                            </div>
                                        </div>
                                    )}

                                    {/* Invalid Values Display */}
                                    {difference.details?.invalidValues && Array.isArray(difference.details.invalidValues) && (
                                        <div className="mt-2 p-2 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-md">
                                            <div className="flex items-center gap-2 mb-1">
                                                <div className="icon-[ph--x-circle] h-3 w-3 text-warning-600 dark:text-warning-400" />
                                                <span className="text-xs font-medium text-warning-800 dark:text-warning-200 uppercase tracking-wide">
                                                    {t('Pages:ScoresheetPage.InvalidValues', 'Invalid Values')}
                                                </span>
                                            </div>
                                            <div className="flex flex-wrap gap-1">
                                                {difference.details.invalidValues.map((val: any, idx: number) => (
                                                    <span key={idx} className="bg-warning-100 dark:bg-warning-800 text-warning-800 dark:text-warning-200 px-2 py-1 rounded text-xs font-mono">
                                                        {field ? <FormattedValue value={val} field={field} /> : String(val)}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className={`border rounded-lg overflow-hidden ${getSectionStatusBg(sectionPercentage)}`}>
            <div
                className="p-4 cursor-pointer"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={`${getSectionStatusIcon(sectionPercentage)} h-5 w-5 ${getSectionStatusColor(sectionPercentage)}`} />
                        <div>
                            <h3 className="text-lg font-semibold font-header text-content-900 dark:text-content-100 capitalize">
                                {sectionResult.section}
                            </h3>
                            <div className="text-sm font-sans text-content-600 dark:text-content-400">
                                {sectionResult.passedChecks}/{sectionResult.totalChecks} checks passed ({sectionPercentage.toFixed(0)}%)
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {sectionResult.differences.length > 0 && (
                            <div className="text-sm font-sans text-error-600 dark:text-error-400 font-medium">
                                {sectionResult.differences.length} {t('Pages:ScoresheetPage.Issues', 'issues')}
                            </div>
                        )}
                        <div className={`icon-[ph--caret]-${isExpanded ? 'up' : 'down'} h-5 w-5 text-content-600 dark:text-content-400 transition-transform`} />
                    </div>
                </div>
            </div>

            {isExpanded && (
                <div className="border-t border-content-200 dark:border-content-700 bg-white dark:bg-content-800">
                    {/* All Field Values */}
                    {(() => {
                        const schemaSection = getSchemaSection();
                        if (!schemaSection) {
                            return (
                                <div className="p-4 text-center text-content-600 dark:text-content-400">
                                    {t('Pages:ScoresheetPage.SchemaNotFound', 'Schema section not found')}
                                </div>
                            );
                        }

                        return (
                            <div className="p-4">
                                <h4 className="text-md font-medium font-header text-content-900 dark:text-content-100 mb-4">
                                    {t('Pages:ScoresheetPage.JudgeEvaluations', 'Judge Evaluations')}
                                </h4>

                                {schemaSection.subsections.map((subsection) => {
                                    const fieldsWithValues = subsection.fields.map(field => ({
                                        field,
                                        rendered: renderFieldValue(field, schemaSection.key, subsection.key)
                                    })).filter(item => item.rendered !== null);

                                    if (fieldsWithValues.length === 0) {
                                        return null;
                                    }

                                    return (
                                        <div key={subsection.key} className="mb-6">
                                            <h5 className="text-sm font-medium font-sans text-content-800 dark:text-content-200 mb-3 capitalize">
                                                {subsection.label}
                                            </h5>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {fieldsWithValues.map(({ rendered }) => rendered)}
                                            </div>
                                        </div>
                                    );
                                })}

                                {schemaSection.subsections.every(subsection =>
                                    subsection.fields.every(field =>
                                        renderFieldValue(field, schemaSection.key, subsection.key) === null
                                    )
                                ) && (
                                        <div className="text-center text-content-600 dark:text-content-400 py-8">
                                            <div className="icon-[ph--clipboard-text] h-8 w-8 mx-auto mb-2 opacity-50" />
                                            <div className="font-sans">{t('Pages:ScoresheetPage.NoEvaluations', 'No evaluations found for this section')}</div>
                                        </div>
                                    )}
                            </div>
                        );
                    })()}

                    {/* Subsection Results Summary */}
                    {sectionResult.subsectionResults.length > 0 && (
                        <div className="p-4 border-t border-content-200 dark:border-content-700 bg-content-50 dark:bg-content-700/30">
                            <h4 className="text-md font-medium font-header text-content-900 dark:text-content-100 mb-3">
                                {t('Pages:ScoresheetPage.SubsectionSummary', 'Subsection Summary')}
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 font-sans">
                                {sectionResult.subsectionResults.map((subsection) => {
                                    const subsectionPercentage = (subsection.passedChecks / subsection.totalChecks) * 100;
                                    return (
                                        <div key={subsection.subsection} className="p-3 bg-white dark:bg-content-800 rounded-md border border-content-200 dark:border-content-700">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <div className={`h-2 w-2 rounded-full ${subsectionPercentage >= 80 ? 'bg-success-500' :
                                                        subsectionPercentage >= 60 ? 'bg-warning-500' : 'bg-error-500'
                                                        }`} />
                                                    <span className="text-sm font-medium text-content-800 dark:text-content-200 capitalize">
                                                        {subsection.subsection}
                                                    </span>
                                                </div>
                                                <span className="text-xs text-content-600 dark:text-content-400">
                                                    {subsection.passedChecks}/{subsection.totalChecks}
                                                </span>
                                            </div>
                                            {subsection.differences.length > 0 && (
                                                <div className="text-xs text-error-600 dark:text-error-400 mt-1">
                                                    {subsection.differences.length} {t('Pages:ScoresheetPage.Issues', 'issues')}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SectionComparisonView; 